import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import type { PortfolioSite } from '../types';
interface PortfolioModalProps {
  site: PortfolioSite | null;
  onClose: () => void;
  onCta: () => void;
}
export function PortfolioModal({
  site,
  onClose,
  onCta
}: PortfolioModalProps) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!site) return;
    setLoaded(false);
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [site, onClose]);
  return <AnimatePresence data-id="element-256" data-mp="275ao10q7wa9x">
      {site ? <motion.div className="fixed inset-0 z-[2000] flex items-center justify-center bg-brand-deep/95 p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} onClick={event => {
      if (event.target === event.currentTarget) onClose();
    }} role="dialog" aria-modal="true" aria-label={`Vista previa de ${site.name}`} data-id="element-257" data-mp="1opxy8tet48u8">
          <motion.div className="flex max-h-[90vh] w-full max-w-[1000px] flex-col overflow-hidden rounded-3xl bg-white shadow-[0_50px_100px_rgba(0,0,0,0.5)]" initial={{
        scale: 0.95,
        y: 20
      }} animate={{
        scale: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-258" data-mp="22pzb778cj6q2">
            <div className="flex flex-shrink-0 items-center justify-between gap-3 bg-brand-deep px-5 py-3.5" data-id="element-259" data-mp="najp9ovewl9n">
              <div className="flex flex-1 items-center gap-3" data-id="element-260" data-mp="ic8hrkslamul">
                <div className="hidden gap-1.5 sm:flex" aria-hidden="true" data-id="element-261" data-mp="jsm90b4znpxj">
                  <span className="h-3 w-3 rounded-full bg-[#FF5F57]" data-id="element-262" data-mp="oqxfqczzlkbj" />
                  <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" data-id="element-263" data-mp="aqmr7gm5ghtj" />
                  <span className="h-3 w-3 rounded-full bg-[#28C840]" data-id="element-264" data-mp="aqd0trx708nb" />
                </div>
                <div className="flex flex-1 items-center gap-2 truncate rounded-lg bg-white/10 px-3.5 py-1.5 text-[13px] text-white/80" data-id="element-265" data-mp="eqn775owd12p">
                  <LockIcon sx={{
                fontSize: 12
              }} data-id="element-266" data-mp="5rjklrqiu3jg" />
                  {site.url.replace('https://', '')}
                </div>
              </div>
              <button type="button" onClick={onClose} aria-label="Cerrar vista previa" className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-267" data-mp="3n9ci4yvvaj3g">
                <CloseIcon sx={{
              fontSize: 18
            }} data-id="element-268" data-mp="2zkmq2dhbu81q" />
              </button>
            </div>

            <div className="relative min-h-[320px] flex-1 overflow-y-auto bg-mist sm:min-h-[440px]" data-id="element-269" data-mp="2zmhazrslm05t">
              {!loaded ? <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-mist" data-id="element-270" data-mp="38lud1zee7mgi">
                  <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-line border-t-brand-blue" data-id="element-271" data-mp="34jhga0hmiyvv" />
                  <span className="text-sm font-medium text-muted" data-id="element-272" data-mp="3ii7ddg4g0yk8">
                    Cargando preview en vivo...
                  </span>
                </div> : null}
              {site.live ? <iframe src={site.url} title={`Sitio web de ${site.name}`} onLoad={() => setLoaded(true)} className="block h-full min-h-[320px] w-full border-0 sm:min-h-[440px]" data-id="element-273" data-mp="3s8iiv73rzft8" /> : <img src={site.image} alt={`Vista previa del sitio web de ${site.name}`} onLoad={() => setLoaded(true)} className="block w-full" data-id="element-274" data-mp="73wsj39xps1" />}
            </div>

            <div className="flex flex-shrink-0 items-center justify-between gap-4 border-t border-line bg-mist px-5 py-3.5" data-id="element-275" data-mp="24baqk9ogvdgp">
              <div className="min-w-0" data-id="element-276" data-mp="28bupnislor5h">
                <p className="truncate text-sm font-bold text-ink" data-id="element-277" data-mp="28ez2f4k4sv7r">
                  {site.name}
                </p>
                <p className="text-xs font-semibold text-brand-blue" data-id="element-278" data-mp="1glovnyf4nkei">
                  {site.nicheLabel}
                </p>
              </div>
              <button type="button" onClick={onCta} className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-brand-yellow px-5 py-2.5 font-display text-sm font-bold text-brand-deep transition hover:-translate-y-px hover:bg-brand-yellow-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2" data-id="element-279" data-mp="1km0ct18a72l8">
                <RocketLaunchIcon sx={{
              fontSize: 16
            }} data-id="element-280" data-mp="3b7uopw2ofmba" />
                ¡Quiero una web así!
              </button>
            </div>
          </motion.div>
        </motion.div> : null}
    </AnimatePresence>;
}